import { c as _c } from "react/compiler-runtime";
import { isString } from 'ramda-adjunct';
import { useHandleLink } from '../hooks/useHandleLink';
import { useEagGridStore } from '../store/useEagGridStore';
import type { ActionCallback } from '../types/ActionCallback';
import type { RowData } from '../types/RowData';
import { getColumnData } from '../utils/getColumnData';
import { getSearchParamsByGridCode } from '../utils/url/getSearchParamsByGridCode';
import { setSourceSearchParamsByGridCode } from '../utils/url/setSourceSearchParamsByGridCode';
type HandleActionClick = (actionKey: string, row: RowData, event: React.MouseEvent) => void;

/**
 * Creates a stable callback for handling row action clicks with per-action locking.
 *
 * Instantiate ONCE in EagGridTable — NOT per-row.
 * The `row` argument is passed per-click, not bound in the hook.
 *
 * Locking behavior:
 * - If callback returns a Promise → auto-lock with reason 'promise', auto-unlock on settle.
 * - If callback calls actionLock.lock() → manual lock with reason 'manual', must unlock manually.
 * - Both can coexist: action stays disabled until ALL locks are released.
 * - Clicks on already-locked actions are silently ignored (double-click protection).
 */
export function useRowActionClick(actionCallback, gridCode, queryIdRef, serverDataRefreshFn) {
  const $ = _c(7);
  const store = useEagGridStore();
  const handleLink = useHandleLink();
  let t0;
  if ($[0] !== actionCallback || $[1] !== gridCode || $[2] !== handleLink || $[3] !== queryIdRef || $[4] !== serverDataRefreshFn || $[5] !== store) {
    t0 = (actionKey, row, event) => {
      if (!actionCallback) {
        return;
      }
      if (store.isActionLocked(row.id, actionKey)) {
        return;
      }
      const rowId = row.id;
      const sourceSearchParams = setSourceSearchParamsByGridCode(gridCode, String(row.rowNumber));
      const searchParams = getSearchParamsByGridCode(gridCode);
      const actionLock = {
        lock: () => store.lockAction(rowId, actionKey, "manual"),
        unlock: () => store.unlockAction(rowId, actionKey, "manual")
      };
      const result = actionCallback({
        actionKey,
        rowId,
        getColumnData: getColumnData(row, gridCode),
        rowData: row,
        refreshData: serverDataRefreshFn,
        refreshCells: _temp,
        redrawRows: _temp2,
        deselectAll: () => store.deselectAll(),
        sourceSearchParams,
        queryId: searchParams?.queryId ?? queryIdRef.current ?? "",
        actionLock
      });
      if (result !== null && result !== undefined && typeof (result as Promise<unknown>).then === "function") {
        store.lockAction(rowId, actionKey, "promise");
        (result as Promise<string | void>).then(actionLink => {
          if (isString(actionLink)) {
            handleLink(actionLink, event.nativeEvent as MouseEvent);
          }
        }).catch(_temp3).finally(() => {
          store.unlockAction(rowId, actionKey, "promise");
        });
      } else {
        if (isString(result)) {
          handleLink(result, event.nativeEvent as MouseEvent);
        }
      }
    };
    $[0] = actionCallback;
    $[1] = gridCode;
    $[2] = handleLink;
    $[3] = queryIdRef;
    $[4] = serverDataRefreshFn;
    $[5] = store;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  return t0;
}
function _temp3() {}
function _temp2() {}
function _temp() {}